/**
 * Профиль потока: сколько купили и продали на каждом уровне за отрезок.
 *
 * Чистый расчёт без холста — его рисует слой графика, а проверяют тесты.
 * Здесь же главный уровень (POC) и зона стоимости (VAH–VAL): они
 * считаются по тому же профилю, и вынести их в отдельные индикаторы
 * значило бы однажды получить главный уровень одного отрезка на
 * профиле другого.
 */

import type { OrderFlowVenue } from '@specter/types';
import type { FlowBarData } from './data.js';

export interface VenueShare {
  buy: number;
  sell: number;
  trades: number;
}

export interface ProfileRow {
  /** Номер строки: уровень потока, сведённый в `group` раз. */
  readonly index: number;
  /** Нижняя граница строки. */
  readonly low: number;
  /** Верхняя граница строки. */
  readonly high: number;
  buy: number;
  sell: number;
  trades: number;
  readonly byVenue: Map<OrderFlowVenue, VenueShare>;
}

export interface FlowProfile {
  /** Строки снизу вверх, без пустых. */
  readonly rows: readonly ProfileRow[];
  readonly buy: number;
  readonly sell: number;
  readonly trades: number;
  /** Самая наторгованная строка. */
  readonly poc: ProfileRow | null;
  /** Зона стоимости: границы по цене. `null` — считать не из чего. */
  readonly valueHigh: number | null;
  readonly valueLow: number | null;
  /** Индексы строк зоны стоимости в `rows`, включительно. */
  readonly valueFrom: number;
  readonly valueTo: number;
  readonly maxVolume: number;
  readonly maxSide: number;
  readonly maxDelta: number;
  /** Медиана объёма строки: от неё меряется «крупный объём». */
  readonly medianVolume: number;
  /** Биржи, поток которых прерывался хотя бы в одном баре отрезка. */
  readonly gaps: ReadonlySet<OrderFlowVenue>;
  readonly bars: number;
}

export const EMPTY_PROFILE: FlowProfile = {
  rows: [],
  buy: 0,
  sell: 0,
  trades: 0,
  poc: null,
  valueHigh: null,
  valueLow: null,
  valueFrom: 0,
  valueTo: -1,
  maxVolume: 0,
  maxSide: 0,
  maxDelta: 0,
  medianVolume: 0,
  gaps: new Set(),
  bars: 0,
};

export function volumeOf(row: { buy: number; sell: number }): number {
  return row.buy + row.sell;
}

/**
 * Собрать профиль.
 *
 * `group` — сколько уровней потока в одной строке: на экране строка не
 * должна быть тоньше пары пикселей, иначе полосы сливаются в шум.
 * `valueArea` — доля объёма в зоне стоимости, 0…1.
 */
export function buildProfile(
  bars: readonly FlowBarData[],
  step: number,
  venues: ReadonlySet<OrderFlowVenue>,
  group: number,
  valueArea: number,
): FlowProfile {
  if (bars.length === 0 || step <= 0) return EMPTY_PROFILE;

  const byIndex = new Map<number, ProfileRow>();
  const gaps = new Set<OrderFlowVenue>();
  const height = step * group;

  for (const bar of bars) {
    for (const venue of bar.gaps) if (venues.has(venue)) gaps.add(venue);
    for (const [venue, levels] of bar.venues) {
      if (!venues.has(venue)) continue;
      for (const [level, cell] of levels) {
        const index = Math.floor(level / group);
        let row = byIndex.get(index);
        if (!row) {
          row = {
            index,
            low: index * height,
            high: (index + 1) * height,
            buy: 0,
            sell: 0,
            trades: 0,
            byVenue: new Map(),
          };
          byIndex.set(index, row);
        }
        row.buy += cell[0]!;
        row.sell += cell[1]!;
        row.trades += cell[2]!;
        let share = row.byVenue.get(venue);
        if (!share) {
          share = { buy: 0, sell: 0, trades: 0 };
          row.byVenue.set(venue, share);
        }
        share.buy += cell[0]!;
        share.sell += cell[1]!;
        share.trades += cell[2]!;
      }
    }
  }

  const rows = [...byIndex.values()]
    .filter((row) => volumeOf(row) > 0)
    .sort((a, b) => a.index - b.index);
  if (rows.length === 0) return { ...EMPTY_PROFILE, gaps, bars: bars.length };

  let buy = 0;
  let sell = 0;
  let trades = 0;
  let pocAt = 0;
  let maxVolume = 0;
  let maxSide = 0;
  let maxDelta = 0;
  rows.forEach((row, i) => {
    buy += row.buy;
    sell += row.sell;
    trades += row.trades;
    const volume = volumeOf(row);
    /*
     * При равенстве побеждает строка ближе к середине профиля.
     *
     * Два одинаковых максимума на краях диапазона — редкость, но первый
     * попавшийся ставил бы главный уровень на край, где торговли
     * заведомо меньше смысла, и прыгал бы от кадра к кадру.
     */
    if (
      volume > maxVolume ||
      (volume === maxVolume && Math.abs(i - rows.length / 2) < Math.abs(pocAt - rows.length / 2))
    ) {
      maxVolume = volume;
      pocAt = i;
    }
    maxSide = Math.max(maxSide, row.buy, row.sell);
    maxDelta = Math.max(maxDelta, Math.abs(row.buy - row.sell));
  });

  const [valueFrom, valueTo] = valueAreaOf(rows, pocAt, valueArea);
  const volumes = rows.map(volumeOf).sort((a, b) => a - b);

  return {
    rows,
    buy,
    sell,
    trades,
    poc: rows[pocAt]!,
    valueHigh: valueArea > 0 ? rows[valueTo]!.high : null,
    valueLow: valueArea > 0 ? rows[valueFrom]!.low : null,
    valueFrom,
    valueTo,
    maxVolume,
    maxSide,
    maxDelta,
    medianVolume: volumes[Math.floor(volumes.length / 2)]!,
    gaps,
    bars: bars.length,
  };
}

/**
 * Зона стоимости: от главного уровня в обе стороны, пока не набрана доля.
 *
 * Классический способ: на каждом шаге расширяться туда, где следующая
 * строка тяжелее. Пустые ценовые ступени между строками не считаются —
 * расширение идёт по наторгованным уровням, а границы берутся по цене.
 */
export function valueAreaOf(
  rows: readonly { buy: number; sell: number }[],
  poc: number,
  share: number,
): [number, number] {
  if (rows.length === 0) return [0, -1];
  const total = rows.reduce((sum, row) => sum + volumeOf(row), 0);
  const target = total * Math.min(1, Math.max(0, share));

  let low = poc;
  let high = poc;
  let sum = volumeOf(rows[poc]!);
  while (sum < target && (low > 0 || high < rows.length - 1)) {
    const below = low > 0 ? volumeOf(rows[low - 1]!) : -1;
    const above = high < rows.length - 1 ? volumeOf(rows[high + 1]!) : -1;
    if (above >= below) {
      high += 1;
      sum += above;
    } else {
      low -= 1;
      sum += below;
    }
  }
  return [low, high];
}

/**
 * Как главный уровень сдвигался по ходу отрезка.
 *
 * На каждом баре — главный уровень всего, что наторговано от начала
 * отрезка до этого бара включительно. Линия показывает, куда перетекал
 * интерес: ровная — рынок принимает цену, ступеньками — ищет новую.
 */
export function pocHistory(
  bars: readonly FlowBarData[],
  step: number,
  venues: ReadonlySet<OrderFlowVenue>,
  group: number,
): Array<{ readonly t: number; readonly price: number }> {
  if (step <= 0) return [];
  const volume = new Map<number, number>();
  const out: Array<{ t: number; price: number }> = [];
  let bestIndex: number | null = null;
  let bestVolume = 0;
  const height = step * group;

  for (const bar of bars) {
    for (const [venue, levels] of bar.venues) {
      if (!venues.has(venue)) continue;
      for (const [level, cell] of levels) {
        const index = Math.floor(level / group);
        const next = (volume.get(index) ?? 0) + cell[0]! + cell[1]!;
        volume.set(index, next);
        if (next > bestVolume) {
          bestVolume = next;
          bestIndex = index;
        }
      }
    }
    if (bestIndex !== null) out.push({ t: bar.t, price: (bestIndex + 0.5) * height });
  }
  return out;
}
